import { fetchApi } from '@/shared/api/fetchApi';
import { Car } from './carsApi';
import { Client } from '@/features/clients/api/clientsApi';
import { Employee } from '@/features/profile/types/profile.types';

export interface CarTechProfileDetail {
  detail: string;
  name: string;
  group: string | null;
  zone: Record<string, unknown>;
}

export interface CarReportEmployee {
  id: string;
  first_name: string;
  last_name: string;
}

export interface CarTechProfile {
  id: string;
  car: string;
  model: string;
  name: string;
  images: Array<{
    type: string;
    order: number;
    file_name: string;
    image_url: string | null;
  }>;
  details: CarTechProfileDetail[];
}

export interface ReportComment {
  id?: string;
  text: string;
  file_name: string;
  original_file_name: string;
  employee: string;
  created_at: string;
  file_url?: string;
}

export interface ReportDetail {
  detail: string;
  comments: ReportComment[];
}

export interface CreateReportComment {
  text: string;
}

export interface CreateReportDetail {
  detail: string;
  comments: CreateReportComment[];
}

export interface CreateReportDto {
  renting?: string;
  activity?: string;
  activity_type?: 'delivery_to_client' | 'pickup_from_client' | 'handoff_to_service' | 'receiving_from_service';
  mileage: number;
  fuel_level: number;
  details?: CreateReportDetail[];
  employee?: string;
  charge_level?: number;
}

export interface CarReport {
  id: string;
  car: Car;
  renting: {
    id: string;
    number: number;
    status: 'draft' | 'confirmed' | 'cancelled';
    car: string;
    client: Client;
    start_date: string;
    start_time: string;
    end_date: string;
    end_time: string;
    address_to_deliver: string;
    address_to_pickup: string;
    deliver_latitude: string;
    deliver_longitude: string;
    pickup_latitude: string;
    pickup_longitude: string;
    total_price: string;
    currency: string;
    original_price: string;
    created_at: string;
    parent: string | null;
  };
  activity: 'delivery_to_client' | 'pickup_from_client';
  tech_profile: string;
  employee: CarReportEmployee;
  mileage: number;
  fuel_level: number;
  charge_level: number;
  created_at: string;
  details: ReportDetail[];
}

export interface ReportsListResponse {
  total: number;
  offset: number;
  limit: number;
  data: Array<{
    id: string;
    car: {
      id: string;
      slug: string;
      color: string;
      model: {
        id: string;
        slug: string;
        brand_name: string;
        model_name: string;
        transmission_type: string;
        body_type: string;
        description: string;
        image: string;
        image_url: string;
      };
      release_year: number;
      registration_number: string;
      description: string;
      price: string;
      image: string;
      image_url: string;
    };
    renting: {
      id: string;
      number: number;
      status: string;
      car: string;
      client: {
        id: string;
        client_type: string;
        first_name: string;
        last_name: string;
        email: string;
        phone: string;
        post_index: string;
        country: string;
        city: string;
        address: string;
        language: string;
        verification_status: string;
        is_email_confirmed: boolean;
        is_phone_confirmed: boolean;
        registered_at: string;
      };
      start_date: string;
      start_time: string;
      end_date: string;
      end_time: string;
      address_to_deliver: string;
      address_to_pickup: string;
      deliver_latitude: string;
      deliver_longitude: string;
      pickup_latitude: string;
      pickup_longitude: string;
      total_price: string;
      currency: string;
      original_price: string;
      created_at: string;
      parent: string | null;
    };
    activity: 'delivery_to_client' | 'pickup_from_client';
    tech_profile: string;
    employee: CarReportEmployee;
    mileage: number;
    fuel_level: number;
    charge_level: number;
    created_at: string;
    details: ReportDetail[];
  }>;
}

export const carReportsApi = {
  getTechProfile: async (carId: string): Promise<CarTechProfile> => {
    return fetchApi<CarTechProfile>(`/api/v1/cars/${carId}/tech_profile`);
  },

  createReport: async (data: CreateReportDto): Promise<CarReport> => {
    return fetchApi<CarReport>('/api/v1/reports/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getReports: async (): Promise<ReportsListResponse> => {
    return fetchApi<ReportsListResponse>('/api/v1/reports/list');
  },

  getReport: async (id: string): Promise<CarReport> => {
    return fetchApi<CarReport>(`/api/v1/reports/${id}`);
  },

  updateReport: async (id: string, data: Partial<CreateReportDto>): Promise<CarReport> => {
    return fetchApi<CarReport>(`/api/v1/reports/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  uploadReportImage: async (reportId: string, detail: string, file: File): Promise<ReportComment> => {
    const formData = new FormData();
    formData.append('file', file);

    return fetchApi<ReportComment>(`/api/v1/reports/${reportId}/${detail}/image/upload`, {
      method: 'POST',
      body: formData,
      headers: undefined,
    });
  },
};
